import {useContext, useRef, useState} from 'react';
import { useRegistrationService } from '../service/registrationService';
import {
  CustomerUpdate,
  DeviceUpdate,
  HeatingSetupUpdate,
  LocationUpdate,
  Registration, RegistrationConflictType
} from '../types/registrationTypes';
import {ApiErrorHelper, RegistrationConflictError} from '../validation/Errors';
import { RegistrationsContext } from './contextProvider';
import {useCurrentPartner} from "../pages/registration/hooks/useCurrentPartner";

export function useRegistrationsState() {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCompletedRegistrationsMetaData, setIsLoadingCompletedRegistrationsMetaData] = useState<boolean>(false);
  const loading = useRef<boolean>(false);
  const loadingCompletedRegistrationsMetaData = useRef<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [registrationsHasBeenFetched, setRegistrationsHasBeenFetched] = useState<boolean>(false);
  const [completedHasBeenFetched, setCompletedHasBeenFetched] = useState<boolean>(false);
  const [hasLoadedAnyCompletedRegistrationsMetaData, setHasLoadedAnyCompletedRegistrationsMetaData] = useState<boolean>(false);
  const currentDate = new Date();
  const fiveHundredYearsAgo = new Date();
  fiveHundredYearsAgo.setFullYear(currentDate.getFullYear() - 500);
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(currentDate.getMonth() - 3);
  const [activeRegistration, setActiveRegistration] = useState<Registration | null>(null);
  const [completedRegistrationsMetaData, setCompletedRegistrationsMetaData] = useState<Registration[]>([]);
  const {
    getRegistration,
    getRegistrations,
    getCompletedRegistrationsMetaData,
    postCompleteRegistration,
    postCustomer,
    postLocation,
    postSystemDescription,
    postDevices,
    postRegistration,
    deleteRegistration
  } = useRegistrationService();

  const partner = useCurrentPartner();
  const { devices } = useContext(RegistrationsContext);
  const { fetchDevices } = devices;
  const { reopenCompletedRegistration } = useRegistrationService();

  const uniqueRegistrations = (previousReservations: Registration[], newRegistrations: Registration[]): Registration[] => {
    const combined = [...previousReservations, ...newRegistrations];
    const map = new Map(combined.map((item) => [item.id, item]));
    return [...map.values()];
  };

  const fetchRegistration = async (registrationId: Registration['id']): Promise<Registration> => {
    try {
      setIsLoading(true);
      const registration = await getRegistration(registrationId);
      setRegistrations([...registrations, registration]);
      setIsLoading(false);
      return registration;
    } catch (e) {
      throw e;
    }
  };

  const updateStateRegistration = (updatedRegistration: Registration) => {
    const updatedRegistrations = registrations.map((reg: Registration) => (reg.id === updatedRegistration.id ? updatedRegistration : reg));
    // If updated registration is not in the list, add it
    if (!updatedRegistrations.find((reg) => reg.id === updatedRegistration.id)) {
      updatedRegistrations.push(updatedRegistration);
    }
    setRegistrations(updatedRegistrations);
  };

  const fetchAllRegistrations = async (): Promise<void> => {
    if (loading.current) {
      return;
    }

    loading.current = true;
    setIsLoading(true);

    setRegistrations([]);

    try {
      const fetchedRegistrations = await getRegistrations();
      setRegistrations((prev) => uniqueRegistrations(prev, fetchedRegistrations));
    } catch (e) {
      setError(true);
      throw e;
    } finally {
      setRegistrationsHasBeenFetched(true);
      loading.current = false;
      setIsLoading(false);
    }
  };

  const removeActiveRegistration = () => {
    setActiveRegistration(null);
  };

  const resetRegistrations = () => {
    setRegistrations([]);
    setActiveRegistration(null);
    setCompletedRegistrationsMetaData([]);
    setRegistrationsHasBeenFetched(false);
    setCompletedHasBeenFetched(false);
    setHasLoadedAnyCompletedRegistrationsMetaData(false);
    setError(false);
    setIsLoading(false);
    setIsLoadingCompletedRegistrationsMetaData(false);
  };

  const removeRegistration = (id: string) => {
    setRegistrations(registrations.filter((registration) => registration.id !== id));
  };

  const addRegistration = (registration: Registration) => {
    setRegistrations([...registrations, registration]);
  };
  const createRegistration = async (registration: Registration): Promise<Registration> => {
    try {
      // Remove id to create a new registration because id is not allowed in backend.
      //@ts-ignore
      registration.id = undefined;
      const newRegistration = await postRegistration(registration);
      setRegistrations([...registrations, newRegistration]);
      setActiveRegistration(newRegistration);
      return newRegistration;
    } catch (e) {
      console.log('Error creating registration', e);
      throw e;
    }
  };

  const deleteRegistrationAction = async (registrationId: string): Promise<void> => {
    try {
      await deleteRegistration(registrationId);
      setRegistrations(registrations.filter((registration) => registration.id !== registrationId));
      setActiveRegistration(null);
    } catch (e) {
      throw e;
    }
  };

  const updateAndSetActive = async (registration: Registration): Promise<Registration> => {
    updateStateRegistration(registration);
    setActiveRegistration(registration);
    return registration;
  }

  const handleRegistrationError = async (registration: Registration, error: any) : Promise<void> => {
    if (ApiErrorHelper.isNotFound(error)) {
      setRegistrations(registrations.filter((reg) => reg.id !== registration.id));
      return;
    }
    if (error instanceof RegistrationConflictError && error.isConflict) {
      // Handle conflict error
      const isDeviceConflict = await error.isDeviceConflict();
      const isRegistrationConflict = await error.isRegistrationConflict();
      // If no data is received, we cannot determine the type of conflict
      if (error.isRegistrationCompleted) {
        // Move the registration from the list of registrations to completed registrations
        console.error('Registration completed error:', error);
        setCompletedRegistrationsMetaData([...completedRegistrationsMetaData, registration]);
        setRegistrations(registrations.filter((reg) => reg.id !== registration.id));
      }
      if (isRegistrationConflict) {
        // Set registration conflict details on the registration
        console.error('Registration conflict error:', error);
        registration.conflict = error.data;
        registration.conflictType = RegistrationConflictType.registration;
      }
      else if (isDeviceConflict) {
        // Set device conflict details on the registration
        console.error('Registration device conflict error:', error);
        registration.conflict = error.data;
        registration.conflictType = RegistrationConflictType.device;
      }
      // Update the registration with conflict details
      await updateAndSetActive(registration);
    } else {
      // Other error
      console.error('Error updating registration:', error);
    }
  }
  
  const updateCustomer = async (registration: Registration) : Promise<Registration> => {
    const updateCustomer: CustomerUpdate = {
      ...registration.customer,
      registrationDtoRevision: registration.revision
    };
    try {
      const updatedRegistration = await postCustomer(registration.id, updateCustomer);
      return updateAndSetActive(updatedRegistration);
    } catch (e) {
      await handleRegistrationError(registration, e);
      throw e;
    }
  }

  const updateLocation = async (registration: Registration) : Promise<Registration> => {
    const updateLocation: LocationUpdate = {
      ...registration.location,
      registrationDtoRevision: registration.revision
    };
    try {
      const updatedRegistration = await postLocation(registration.id, updateLocation);
      return updateAndSetActive(updatedRegistration);
    } catch (e) {
      await handleRegistrationError(registration, e);
      throw e;
    }
  }

  const updateSystemDescription = async (registration: Registration) : Promise<Registration> => {
    const updateHeatingSetup: HeatingSetupUpdate = {
      ...registration.heatingSetup,
      registrationDtoRevision: registration.revision
    };
    try {
      const updatedRegistration = await postSystemDescription(registration.id, updateHeatingSetup);
      return updateAndSetActive(updatedRegistration);
    } catch (e) {
      await handleRegistrationError(registration, e);
      throw e;
    }
  }

  const updateDevices = async (registration: Registration) : Promise<Registration> => {
    const updateDevices: DeviceUpdate = {
      deviceIds: registration.devices?.items.map(device => device.id) || [],
      registrationDtoRevision: registration.revision
    };
    try {
      const updatedRegistration = await postDevices(registration.id, updateDevices);
      return updateAndSetActive(updatedRegistration);
    } catch (e) {
      await handleRegistrationError(registration, e);
      throw e;
    }
  }
  
  const completeRegistration = async (): Promise<void> => {
    if (!activeRegistration) {
      throw new Error('Missing registration');
    }
    try {
      await postCompleteRegistration(activeRegistration);
    } catch (error) {
      await handleRegistrationError(activeRegistration, error);
      throw error;
    }
  };

  const reopenCompletedRegistrationAction = async (registrationId: string): Promise<void> => {
    try {
      await reopenCompletedRegistration(registrationId);
      const updatedRegistration = await getRegistration(registrationId);
      setCompletedRegistrationsMetaData(completedRegistrationsMetaData.filter((reg) => reg.id !== registrationId));
      setRegistrations([...registrations, updatedRegistration]);
      setActiveRegistration(updatedRegistration);
      fetchDevices();
    } catch (e) {
      console.log('Error reopening completed registration', e);
      throw e;
    }
  };

  const fetchAllCompletedRegistrationsMetaData = async (): Promise<void> => {
    if (partner.id === '') {
      return;
    }
    if (loadingCompletedRegistrationsMetaData.current) {
      return;
    }

    loadingCompletedRegistrationsMetaData.current = true;
    setIsLoadingCompletedRegistrationsMetaData(true);

    setCompletedRegistrationsMetaData([]);

    try {
      setIsLoadingCompletedRegistrationsMetaData(true);
      const completedRegistrationsMetaDataFetch = await getCompletedRegistrationsMetaData()
      setCompletedRegistrationsMetaData((prev) => uniqueRegistrations(prev, completedRegistrationsMetaDataFetch));
      setHasLoadedAnyCompletedRegistrationsMetaData(true);
    } catch (e) {
      console.log('Error fetching completed registrations', e);
      throw e;
    } finally {
      setCompletedHasBeenFetched(true);
      loadingCompletedRegistrationsMetaData.current = false;
      setIsLoadingCompletedRegistrationsMetaData(false);
    }
  };

  return {
    registrations,
    isLoading,
    error,
    activeRegistration,
    isLoadingCompletedRegistrationsMetaData,
    completedRegistrationsMetaData,
    registrationsHasBeenFetched,
    completedHasBeenFetched,
    hasLoadedAnyCompletedRegistrationsMetaData,
    setActiveRegistration,
    removeRegistration,
    addRegistration,
    fetchAllRegistrations,
    fetchRegistration,
    completeRegistration,
    createRegistration,
    deleteRegistrationAction,
    reopenCompletedRegistrationAction,
    fetchAllCompletedRegistrationsMetaData,
    removeActiveRegistration,
    resetRegistrations,
    updateCustomer,
    updateLocation,
    updateSystemDescription,
    updateDevices
  };
}
